import Trix from "trix"

const ATTRIBUTE_NAME = "textAlignment"
const ACTION_PREFIX = "x-align-"
const ALIGNMENT_STYLES = {
  center: "display: block; width: 100%; text-align: center;",
  right: "display: block; width: 100%; text-align: right;"
}
const LABELS = {
  de: {
    left: "Linksbündig",
    center: "Zentriert",
    right: "Rechtsbündig"
  },
  en: {
    left: "Align left",
    center: "Align center",
    right: "Align right"
  }
}

Trix.config.blockAttributes[ATTRIBUTE_NAME] = {
  tagName: "span",
  group: false,
  htmlAttributes: ["style"],
  test(element) {
    return element.style.display === "block" &&
      Object.keys(ALIGNMENT_STYLES).includes(element.style.textAlign)
  }
}

document.addEventListener("trix-initialize", (event) => {
  installAlignmentButtons(event.target)
  updateAlignmentButtons(event.target)
})

document.addEventListener("trix-action-invoke", (event) => {
  if (!event.actionName.startsWith(ACTION_PREFIX)) return

  const alignment = event.actionName.slice(ACTION_PREFIX.length)
  applyAlignment(event.target, alignment)
  event.preventDefault()
})

document.addEventListener("trix-selection-change", (event) => {
  updateAlignmentButtons(event.target)
})

document.addEventListener("trix-change", (event) => {
  updateAlignmentButtons(event.target)
})

function installAlignmentButtons(editorElement) {
  const toolbar = editorElement.toolbarElement
  if (!toolbar || toolbar.querySelector("[data-trix-button-group='alignment-tools']")) return

  const textTools = toolbar.querySelector("[data-trix-button-group='text-tools']")
  if (!textTools) return

  const labels = alignmentLabels()
  const group = document.createElement("span")
  group.className = "trix-button-group trix-button-group--alignment-tools"
  group.dataset.trixButtonGroup = "alignment-tools"
  group.innerHTML = ["left", "center", "right"].map((alignment) => `
    <button type="button"
            class="trix-button trix-button--icon trix-button--icon-align-${alignment}"
            data-trix-action="${ACTION_PREFIX}${alignment}"
            title="${labels[alignment]}"
            aria-label="${labels[alignment]}"
            aria-pressed="false"
            tabindex="-1">${labels[alignment]}</button>
  `).join("")

  textTools.insertAdjacentElement("afterend", group)
}

function applyAlignment(editorElement, alignment) {
  if (!["left", ...Object.keys(ALIGNMENT_STYLES)].includes(alignment)) return

  const editor = editorElement.editor
  const selectedRange = editor.getSelectedRange()
  let document = editor.getDocument()
  const selectedBlockIndexes = blockIndexesForRange(document, selectedRange)
  const blockRange = rangeForBlockIndexes(document, selectedBlockIndexes)

  editor.recordUndoEntry(alignmentLabels()[alignment])
  document = document.removeAttributeAtRange(ATTRIBUTE_NAME, blockRange)

  if (alignment !== "left") {
    document = document.addAttributeAtRange(ATTRIBUTE_NAME, true, blockRange)

    selectedBlockIndexes.forEach((index) => {
      const position = document.positionFromLocation({ index, offset: 0 })
      document = document.setHTMLAttributeAtPosition(position, "style", ALIGNMENT_STYLES[alignment])
    })
  }

  editor.composition.setDocument(document)
  editor.setSelectedRange(selectedRange)
  updateAlignmentButtons(editorElement)
}

function blockIndexesForRange(document, range) {
  const [startLocation, endLocation] = document.locationRangeFromRange(range)
  let endIndex = endLocation.index

  if (range[0] !== range[1] && endLocation.offset === 0) {
    endIndex = Math.max(startLocation.index, endIndex - 1)
  }

  return Array.from(
    { length: endIndex - startLocation.index + 1 },
    (_, offset) => startLocation.index + offset
  )
}

function rangeForBlockIndexes(document, blockIndexes) {
  const startIndex = blockIndexes[0]
  const endIndex = blockIndexes[blockIndexes.length - 1]
  const endBlock = document.getBlockAtIndex(endIndex)

  return [
    document.positionFromLocation({ index: startIndex, offset: 0 }),
    document.positionFromLocation({ index: endIndex, offset: endBlock.getLength() })
  ]
}

function updateAlignmentButtons(editorElement) {
  const toolbar = editorElement.toolbarElement
  if (!toolbar || !editorElement.editor) return

  const alignment = alignmentForSelection(editorElement.editor)

  toolbar.querySelectorAll("[data-trix-button-group='alignment-tools'] [data-trix-action]").forEach((button) => {
    const active = button.dataset.trixAction === `${ACTION_PREFIX}${alignment}`
    button.classList.toggle("trix-active", active)
    button.setAttribute("aria-pressed", active.toString())
  })
}

function alignmentForSelection(editor) {
  const document = editor.getDocument()
  const blockIndexes = blockIndexesForRange(document, editor.getSelectedRange())
  const alignments = blockIndexes.map((index) => alignmentForBlock(document.getBlockAtIndex(index)))

  return alignments.every((alignment) => alignment === alignments[0]) ? alignments[0] : null
}

function alignmentForBlock(block) {
  if (!block.getAttributes().includes(ATTRIBUTE_NAME)) return "left"

  return block.htmlAttributes.style?.match(/text-align\s*:\s*(center|right)/i)?.[1].toLowerCase() || "left"
}

function alignmentLabels() {
  const language = document.documentElement.lang || navigator.language || "en"
  return LABELS[language.toLowerCase().startsWith("de") ? "de" : "en"]
}
